import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tabs, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getStockTakesApi, createStockTakeApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';
import Breadcrumbs from 'components/Breadcrumbs';

const StockTakeList = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [conductedDate, setConductedDate] = useState('');
  const [createNotes, setCreateNotes] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = { page: pageSettings.page, page_size: pageSettings.pageSize };
    if (statusFilter) params.status = statusFilter;
    getStockTakesApi(params).then(response => {
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pageSettings.page, pageSettings.pageSize, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleCreate = () => {
    if (!selectedWarehouse || !conductedDate) {
      toast.error('Warehouse and date are required', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setCreateLoading(true);
    createStockTakeApi({
      warehouse: selectedWarehouse.value,
      conducted_date: conductedDate,
      notes: createNotes
    }).then(response => {
      toast.success('Stock take created', { position: "top-right", autoClose: 3000, transition: Bounce });
      setCreateLoading(false);
      setShowCreateModal(false);
      const newId = response.data?.data?.id;
      if (newId) navigate(`/uniform-management/stock-take/${newId}`);
      else fetchData();
    }).catch(e => {
      setCreateLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Failed to create stock take';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const columns = [
    {
      title: 'Warehouse', dataIndex: 'warehouse_name',
      render: (text, record) => (
        <a onClick={() => navigate(`/uniform-management/stock-take/${record.id}`)}
          style={{ cursor: 'pointer', color: '#D3001A' }}>{text}</a>
      )
    },
    { title: 'Conducted By', dataIndex: 'conducted_by_name' },
    { title: 'Date', dataIndex: 'conducted_date', render: (d) => formatDateHandler(d) },
    { title: 'Items', dataIndex: 'item_count' },
    {
      title: 'Total Variance', dataIndex: 'total_variance',
      render: (val) => (
        <span style={{ color: val < 0 ? '#ff4d4f' : val > 0 ? '#52c41a' : 'inherit', fontWeight: val !== 0 ? 600 : 400 }}>
          {val > 0 ? `+${val}` : val}
        </span>
      )
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status.replace('_', ' ')}</span>
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button label="View" icon="fa fa-eye" className="btn btn-sm btn-outline-primary"
          onClick={() => navigate(`/uniform-management/stock-take/${record.id}`)} />
      )
    }
  ];

  const tabItems = [
    { key: '', label: 'All' },
    { key: 'IN_PROGRESS', label: 'In Progress' },
    { key: 'COMPLETED', label: 'Completed' },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Stock Takes" />
        <div className="d-flex justify-content-end mb-3">
<Button label="New Stock Take" icon="fa fa-plus" className="btn btn-primary btn-sm"
            onClick={() => { setSelectedWarehouse(null); setConductedDate(''); setCreateNotes(''); setShowCreateModal(true); }} />
        </div>

        <Tabs activeKey={statusFilter} onChange={(key) => { setStatusFilter(key); setPageSettings(prev => ({ ...prev, page: 1 })); }}
          items={tabItems} />

        <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
          pagination={{
            total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
            pageSizeOptions, itemRender, showSizeChanger: true
          }}
          onChange={handleChange} scroll={{ x: "max-content" }} />

        <Modal
          title={
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Create Stock Take</div>
              <div style={{ fontSize: 13, fontWeight: 400, color: '#6B7280', marginTop: 4 }}>
                Snapshot a warehouse's physical stock to reconcile against system records.
              </div>
            </div>
          }
          open={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          width={520}
          centered
          destroyOnClose
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowCreateModal(false)} disabled={createLoading}>
              Cancel
            </button>,
            <Button
              key="submit"
              label="Create Stock Take"
              icon="fa fa-clipboard-check"
              className="btn btn-primary ms-2"
              loading={createLoading}
              disabled={createLoading || !selectedWarehouse || !conductedDate}
              onClick={handleCreate}
            />
          ]}
        >
          <div className="mb-3">
            <label className="col-form-label">Warehouse <span className="text-danger">*</span></label>
            <FilterSelect isClearable onChange={setSelectedWarehouse} optionLabel="label" optionValue="value"
              options={warehouseOptions} value={selectedWarehouse}
              placeholder="Select warehouse to count" isLoading={warehousesLoading} />
          </div>
          <div className="mb-3">
            <label className="col-form-label">Conducted Date <span className="text-danger">*</span></label>
            <input type="date" className="form-control" value={conductedDate}
              onChange={(e) => setConductedDate(e.target.value)} />
          </div>
          <div className="mb-1">
            <label className="col-form-label">Notes <small className="text-muted">(optional)</small></label>
            <textarea className="form-control" rows={3} value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              placeholder="Reason for stock take, special instructions, etc." />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default StockTakeList;
